/**
 * Public memorial — leave a tribute form with honeypot and optional email.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { MEMORIAL_COPY } from '../../utils/memorial';
import { getApiBase } from '../../utils/apiBase';
import { toast } from 'sonner';

const API = getApiBase();

export const TRIBUTE_REACTION_OPTIONS = [
  { key: 'candle', emoji: '🕯️', label: 'Light a candle' },
  { key: 'flower', emoji: '🌹', label: 'Leave a flower' },
  { key: 'love', emoji: '❤️', label: 'Send love' },
  { key: 'dove', emoji: '🕊️', label: 'Peace' },
  { key: 'prayer', emoji: '🙏', label: 'Thinking of you' },
];

export function LeaveTributeForm({
  slug,
  allowTributes = true,
  requireEmail = false,
  onSubmitted,
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [relationship, setRelationship] = useState('');
  const [message, setMessage] = useState('');
  const [reactionType, setReactionType] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!allowTributes) {
    return (
      <div className="memorial-public__card p-6 text-center">
        <p className="text-sm text-[hsl(32_10%_42%)]">Tributes are closed by the family.</p>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/memorial/${slug}/tributes`, {
        author_name: name.trim(),
        message: message.trim(),
        relationship: relationship.trim() || undefined,
        author_email: email.trim() || undefined,
        reaction_type: reactionType || undefined,
        website: honeypot,
      });
      toast.success(data.message || 'Thank you. Your tribute will appear once approved by the family.');
      setMessage('');
      setSubmitted(true);
      onSubmitted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit tribute.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="memorial-public__card p-6 text-center" data-testid="memorial-tribute-thankyou">
        <p className="font-serif text-lg text-[hsl(24_14%_16%)]">Thank you</p>
        <p className="mt-2 text-sm text-[hsl(32_10%_42%)]">
          Your tribute will appear once approved by the family.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="memorial-public__card p-6 sm:p-8"
      data-testid="memorial-leave-tribute-form"
    >
      <h3 className="font-serif text-xl text-[hsl(24_14%_16%)]">{MEMORIAL_COPY.leaveTribute}</h3>
      <p className="mt-1 text-sm text-[hsl(32_10%_42%)]">{MEMORIAL_COPY.leaveTributeHint}</p>

      <div className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden" aria-hidden>
        <label htmlFor="tribute-website">Website</label>
        <input
          id="tribute-website"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>

      <div className="mt-5 space-y-3">
        <div>
          <Label htmlFor="tribute-name">Your name</Label>
          <Input
            id="tribute-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={80}
            className="mt-1.5 bg-white"
          />
        </div>
        <div>
          <Label htmlFor="tribute-rel">Relationship (optional)</Label>
          <Input
            id="tribute-rel"
            value={relationship}
            onChange={(e) => setRelationship(e.target.value)}
            maxLength={80}
            className="mt-1.5 bg-white"
          />
        </div>
        {requireEmail ? (
          <div>
            <Label htmlFor="tribute-email">Email</Label>
            <Input
              id="tribute-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              maxLength={120}
              className="mt-1.5 bg-white"
            />
          </div>
        ) : (
          <div>
            <Label htmlFor="tribute-email-opt">Email (optional, private to the family)</Label>
            <Input
              id="tribute-email-opt"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={120}
              className="mt-1.5 bg-white"
            />
          </div>
        )}
        <div>
          <Label htmlFor="tribute-msg">Message</Label>
          <Textarea
            id="tribute-msg"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            rows={4}
            maxLength={1000}
            className="mt-1.5 resize-none bg-white"
          />
        </div>
        <div>
          <p className="text-xs text-[hsl(32_10%_42%)]">Optional remembrance (with your message)</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {TRIBUTE_REACTION_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setReactionType(reactionType === opt.key ? '' : opt.key)}
                className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                  reactionType === opt.key
                    ? 'border-[hsl(38_36%_42%)] bg-[hsl(38_36%_42%)]/10'
                    : 'border-[hsl(38_14%_86%)] bg-white hover:bg-muted/30'
                }`}
              >
                <span aria-hidden>{opt.emoji}</span>
                <span className="ml-1.5">{opt.label}</span>
              </button>
            ))}
          </div>
        </div>
        <Button
          type="submit"
          className="rounded-full bg-[hsl(152_28%_22%)] hover:bg-[hsl(152_28%_18%)]"
          disabled={submitting}
        >
          {submitting ? 'Sending…' : 'Send tribute'}
        </Button>
      </div>
    </form>
  );
}

export default LeaveTributeForm;
